'use client';

import { addEventParam, INinjaEvent, useQueuedEventStore } from '@ninjacart/nc-events-sdk-v2';
import {
  handleMarketingEvents,
  KissanEventType,
  msToTime,
  sendToFirebasEvents,
  useDevotionalStore,
  useEventZStore,
} from '@ninjacart/nc-nk-store';
import ReactPlayer from 'react-player';
import { useCallback, useEffect, useRef } from 'react';

type Props = {
  url: string;
  videoId: string;
  title: string;
  sessionName?: string;
  type: string;
  screenName: string;
  isAutoPlay?: boolean;
  mutedVideo?: boolean;
  style?: any;
};

export const ReactVideoPlayer = ({
  url,
  videoId,
  title,
  sessionName,
  type,
  screenName,
  isAutoPlay = false,
  mutedVideo = false,
  style = { position: 'absolute', top: 0, left: 0 },
}: Props) => {
  const processEvents = useRef(useQueuedEventStore((s) => s.processEvents)).current;
  const startTimeRef = useRef(performance.now());
  const hasCalledHandleOnPlayProgressOne = useRef(false);
  const isVideoPlayedThirtySeconds = useRef(false);
  const isVideoPlayedTwentySeconds = useRef(false);
  const playerRef = useRef<ReactPlayer>(null);
  const timeElapsedRef = useRef(0);
  const isModeratorActive = useDevotionalStore((store) => store.isModeratorActive);
  const defaultVolumeRef = useRef<number>();
  const volume = 0.1;

  const watchTimeRef = useRef(0); // how long user watched
  const lastProgressTime = useRef<number | null>(null); // last onProgress timestamp
  const isPausedRef = useRef(false); // track pause state
  const hasFiredVideoPlayRef = useRef(false);

  useEffect(() => {
    watchTimeRef.current = 0;
    lastProgressTime.current = null;
    timeElapsedRef.current = 0;
    hasCalledHandleOnPlayProgressOne.current = false;
    isVideoPlayedTwentySeconds.current = false;
    isVideoPlayedThirtySeconds.current = false;
    startTimeRef.current = performance.now();
  }, [url, videoId]);

  const enqueueEvent = useCallback(
    (
      eventName: KissanEventType,
      additionalParams: { key: string; value: any }[] = [],
      screenDetails?: { screen?: string; screenTimeInSecs?: number }
    ) => {
      const additionalDetails = [
        addEventParam('videoId', videoId),
        addEventParam('url', url),
        addEventParam('title', title),
        addEventParam('type', type),
        ...(sessionName
          ? [
              addEventParam('referenceKey1', 'sessionName'),
              addEventParam('referenceValue1', sessionName),
            ]
          : []),
        ...additionalParams,
      ];

      const finalScreenDetails = {
        screen: screenName,
        ...screenDetails,
      };

      const event: INinjaEvent = {
        name: eventName,
        screenDetails: finalScreenDetails,
        eventParams: additionalDetails,
      };

      processEvents([event]);

      if (sendToFirebasEvents.includes(eventName)) {
        handleMarketingEvents(eventName, finalScreenDetails, additionalDetails);
      }
    },
    [processEvents, url, videoId, title, type, screenName]
  );

  const bufferTimeCapture = (calculatedBufferTime) => {
    enqueueEvent(KissanEventType.VIDEO_BUFFER_TIME, [
      addEventParam('bufferTime', calculatedBufferTime),
    ]);
  };

  const handlePlay = () => {
    isPausedRef.current = false;
    if (!hasFiredVideoPlayRef.current) {
      hasFiredVideoPlayRef.current = true;
      enqueueEvent(KissanEventType.VIDEO_PLAY);
      const calculatedBufferTime = msToTime(performance.now() - startTimeRef.current);
      if (calculatedBufferTime) {
        bufferTimeCapture(calculatedBufferTime);
      }
    }
  };

  const handleOnPlayProgressOne = () => {
    enqueueEvent(KissanEventType.AUTO_VIDEO_PLAY_VIDEO_PROGRESS_ONE, [
      addEventParam('videoPlaybackTime', Math.round(watchTimeRef.current)),
    ]);
  };

  const handleOnPlayProgressTwentySeconds = () => {
    enqueueEvent(KissanEventType.AUTO_PLAY_VIDEO_PROGRESS_TWENTY_SECONDS, [
      addEventParam('videoPlaybackTime', Math.round(watchTimeRef.current)),
    ]);
  };

  const handleOnPlayProgressThirtySeconds = () => {
    enqueueEvent(KissanEventType.AUTO_PLAY_VIDEO_PROGRESS_THIRTY_SECONDS, [
      addEventParam('videoPlaybackTime', Math.round(watchTimeRef.current)),
    ]);
  };

  const onProgress = () => {
    const currentTime = performance.now();

    // Stop tracking if tab hidden OR paused
    if (document?.hidden || isPausedRef.current) {
      return;
    }

    if (lastProgressTime.current !== null) {
      const delta = (currentTime - lastProgressTime.current) / 1000; // in seconds
      watchTimeRef.current += delta;
    }

    lastProgressTime.current = currentTime;
    const watched = Math.round(watchTimeRef.current);

    if (isAutoPlay) {
      if (watched >= 10 && !hasCalledHandleOnPlayProgressOne.current) {
        handleOnPlayProgressOne();
        hasCalledHandleOnPlayProgressOne.current = true;
      }

      if (watched >= 20 && !isVideoPlayedTwentySeconds.current) {
        handleOnPlayProgressTwentySeconds();
        isVideoPlayedTwentySeconds.current = true;
      }

      if (watched >= 30 && !isVideoPlayedThirtySeconds.current) {
        handleOnPlayProgressThirtySeconds();
        isVideoPlayedThirtySeconds.current = true;
      }

      if (watched > timeElapsedRef.current + 10) {
        enqueueEvent(KissanEventType.AUTO_VIDEO_PLAY_VIDEO_PROGRESS, [
          addEventParam('videoPlaybackTime', watched),
        ]);
        timeElapsedRef.current = watched;
      }
    } else {
      if (watched > timeElapsedRef.current + 10) {
        enqueueEvent(KissanEventType.VIDEO_PROGRESS, [addEventParam('videoPlaybackTime', watched)]);
        timeElapsedRef.current = watched;
      }
    }
  };

  const handlePause = () => {
    isPausedRef.current = true; // video is paused
    lastProgressTime.current = null;
    hasFiredVideoPlayRef.current = false;
    enqueueEvent(KissanEventType.VIDEO_PAUSED);
  };

  const handleEnded = () => {
    lastProgressTime.current = null;
    const end = performance.now();
    const duration = msToTime(end - startTimeRef.current);
    enqueueEvent(KissanEventType.VIDEO_PLAY_FINISH, [], {
      screenTimeInSecs: Math.round(watchTimeRef.current),
    });
  };

  return (
    <ReactPlayer
      ref={playerRef}
      url={url}
      width="100%"
      height="100%"
      controls={false}
      playing
      playsinline
      // autoPlay={isAutoPlay}
      muted
      volume={isModeratorActive ? volume : (defaultVolumeRef.current ?? undefined)}
      style={style}
      config={{
        youtube: {
          playerVars: {
            rel: 0,
            // showinfo: 0,
            // fs: 0,
            autoplay: 1,
            playsinline: 1,
            controls: 1,
            mute: 1,
            origin: typeof window !== 'undefined' ? window.location.origin : undefined,
          },
        },
      }}
      onReady={(player: ReactPlayer) => {
        startTimeRef.current = performance.now();
        defaultVolumeRef.current = playerRef.current?.getInternalPlayer().getVolume();
        console.log('🚀 ~ defaultVolumeRef.current:', defaultVolumeRef.current);
        enqueueEvent(KissanEventType.VIDEO_ON_READY_TIME);
        player.getInternalPlayer('youtube')?.playVideo();
      }}
      onPlay={handlePlay}
      onPause={handlePause}
      onEnded={handleEnded}
      onBufferEnd={() => {
        if (!hasFiredVideoPlayRef.current) {
          handlePlay();
        }
      }}
      onProgress={onProgress}
      onError={(e) =>
        enqueueEvent(KissanEventType.VIDEO_ERROR, [
          addEventParam('errorMessage', e.toString()),
          addEventParam('error', JSON.stringify(e)),
        ])
      }
    />
  );
};
